import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Zap, Play, ExternalLink, CheckCircle, X } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'
import { OpportunityAccordion, type OpportunityItem } from '../../components/performance/OpportunityAccordion'
import { PagesTable, type PerfPage, type PrevPage } from '../../components/performance/PagesTable'

// ── Types ──────────────────────────────────────────────

interface PerfAudit {
  id: number
  clientId: number
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalUrls: number
  sampledUrls: number
  samplingMode: 'all' | 'sampled'
  samplingNote: string | null
  avgPerfMobile: number | null
  avgPerfDesktop: number | null
  avgSeo: number | null
  avgBp: number | null
  errorMsg: string | null
  createdAt: string
  completedAt: string | null
  pages: PerfPage[]
}

interface ProgressData {
  step: string
  current: number
  total: number
  currentUrl: string | null
}

// ── Helpers ────────────────────────────────────────────

function formatAuditDate(iso: string): string {
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `Audit du ${dd}/${mm}/${yyyy}`
}

// ── Main Component ─────────────────────────────────────

export default function PageSpeedAudit() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const clientId = Number(projectId)

  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null)
  const [isLaunching, setIsLaunching] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)
  // Optimistic: show pending UI immediately after launch, before list refreshes
  const [justLaunched, setJustLaunched] = useState(false)
  // Notification when audit completes
  const [completedNotif, setCompletedNotif] = useState(false)
  const prevStatusRef = useRef<string | null>(null)

  // ── Fetch list of audits ──
  const { data: audits, loading: auditsLoading, refetch: refetchAudits } = useApi<PerfAudit[]>(
    `/api/perf-audits?clientId=${clientId}`
  )

  const auditList = Array.isArray(audits) ? audits : []

  // On mount: auto-select the latest completed audit
  useEffect(() => {
    if (auditList.length === 0) return
    if (selectedAuditId !== null) return
    const completed = auditList.find(a => a.status === 'completed')
    if (completed) setSelectedAuditId(completed.id)
    else setSelectedAuditId(auditList[0].id)
  }, [auditList])

  // ── Active audit (from list — gives us status for polling) ──
  const activeAudit = auditList.find(a => a.id === selectedAuditId) ?? null
  const isRunning = activeAudit?.status === 'running' || activeAudit?.status === 'pending'

  // Clear optimistic state once list catches up
  useEffect(() => {
    if (justLaunched && isRunning) setJustLaunched(false)
  }, [justLaunched, isRunning])

  // Detect audit completion → show notification
  useEffect(() => {
    if (!activeAudit) return
    const prev = prevStatusRef.current
    if (prev && (prev === 'pending' || prev === 'running') && activeAudit.status === 'completed') {
      setCompletedNotif(true)
    }
    prevStatusRef.current = activeAudit.status
  }, [activeAudit?.status])

  // ── Progress (only when running) ──
  const { data: progress, refetch: refetchProgress } = useApi<ProgressData>(
    activeAudit?.status === 'running' && activeAudit
      ? `/api/perf-audits/${activeAudit.id}/progress`
      : ''
  )

  // ── Polling every 3s when running/pending ──
  useEffect(() => {
    if (!isRunning) return
    const interval = setInterval(() => {
      refetchAudits()
      if (activeAudit?.status === 'running') refetchProgress()
    }, 3000)
    return () => clearInterval(interval)
  }, [isRunning, refetchAudits, refetchProgress, activeAudit?.status])

  // ── Detail + opportunities (only when completed) ──
  const isCompleted = activeAudit?.status === 'completed'

  const { data: auditDetail } = useApi<PerfAudit>(
    isCompleted && selectedAuditId ? `/api/perf-audits/${selectedAuditId}` : ''
  )

  const { data: opportunitiesRaw } = useApi<OpportunityItem[]>(
    isCompleted && selectedAuditId ? `/api/perf-audits/${selectedAuditId}/top-opportunities` : ''
  )

  const { data: prevPagesData } = useApi<{ success: boolean; data: PrevPage[] | null }>(
    isCompleted && selectedAuditId ? `/api/perf-audits/${selectedAuditId}/previous-pages` : ''
  )

  const previousPages = prevPagesData?.data ?? null

  const pages = Array.isArray(auditDetail?.pages) ? auditDetail!.pages : []
  const opportunities = Array.isArray(opportunitiesRaw) ? opportunitiesRaw : []

  // Button should be disabled during launch, while running, or during optimistic state
  const launchDisabled = isLaunching || isRunning || justLaunched

  // ── Launch audit ──
  const handleLaunch = useCallback(async () => {
    setIsLaunching(true)
    setLaunchError(null)
    setCompletedNotif(false)
    const { error } = await apiPost('/api/perf-audits', { clientId })
    if (error) {
      setLaunchError(error)
    } else {
      setJustLaunched(true)
      await refetchAudits()
    }
    setIsLaunching(false)
  }, [clientId, refetchAudits])

  // After launch, select the newest pending/running audit
  useEffect(() => {
    if (auditList.length === 0) return
    const inProgress = auditList.find(a => a.status === 'pending' || a.status === 'running')
    if (inProgress && selectedAuditId !== inProgress.id) {
      setSelectedAuditId(inProgress.id)
    }
  }, [auditList])

  // ── Loading skeleton ──
  if (auditsLoading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-[#E2E4ED] rounded-xl p-6 animate-pulse">
            <div className="h-5 bg-[#F4F5FA] rounded w-1/3 mb-3" />
            <div className="h-4 bg-[#F4F5FA] rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }

  // ── Empty state ──
  if (auditList.length === 0) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
            <Zap className="w-7 h-7 text-[#555A73]" strokeWidth={1.25} />
          </div>
          <p className="text-[#1B2559] font-semibold mb-1">Aucun audit</p>
          <p className="text-[#8B8FA7] text-sm mb-6 max-w-sm">
            Lancez un audit PageSpeed complet pour analyser les performances de votre site sur mobile et desktop.
          </p>
          {launchError && (
            <p className="text-red-500 text-sm mb-4">{launchError}</p>
          )}
          <button
            onClick={handleLaunch}
            disabled={launchDisabled}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#6366F1] to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {(isLaunching || justLaunched) ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Play className="w-4 h-4" strokeWidth={1.25} />
            )}
            {isLaunching ? 'Lancement...' : justLaunched ? 'Audit lanc\u00e9...' : 'Lancer un premier audit'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-[#1B2559]">PageSpeed</h1>

        <div className="flex items-center gap-3">
          {/* Audit selector */}
          {auditList.length > 0 && (
            <select
              value={selectedAuditId ?? ''}
              onChange={e => setSelectedAuditId(Number(e.target.value))}
              className="py-1.5 px-3 text-sm bg-white border border-[#E2E4ED] rounded-lg outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-indigo-100 text-[#1B2559] cursor-pointer"
            >
              {auditList.map(a => (
                <option key={a.id} value={a.id}>
                  {formatAuditDate(a.createdAt)}
                  {a.status !== 'completed' ? ` (${a.status})` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Launch button */}
          <button
            onClick={handleLaunch}
            disabled={launchDisabled}
            className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#6366F1] to-[#7C3AED] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {(isLaunching || justLaunched) ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
            )}
            {isLaunching ? 'Lancement...' : justLaunched ? 'Audit lanc\u00e9...' : 'Lancer un audit'}
          </button>
        </div>
      </div>

      {launchError && (
        <p className="text-red-500 text-sm">{launchError}</p>
      )}

      {/* ── Optimistic pending state (before list refreshes) ── */}
      {justLaunched && !isRunning && (
        <div className="max-w-xl bg-white border border-[#E2E4ED] rounded-xl p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#6366F1] animate-pulse" strokeWidth={1.25} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1B2559]">Audit en attente...</h2>
              <p className="text-sm text-[#8B8FA7]">L'audit a &eacute;t&eacute; lanc&eacute; et sera trait&eacute; dans quelques instants.</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Notification: audit terminé ── */}
      {completedNotif && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 animate-in fade-in">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-800">Audit termin&eacute; !</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Les r&eacute;sultats sont disponibles ci-dessous. Consultez aussi{' '}
              <button
                onClick={() => navigate(`/p/${projectId}/performance/vitals`)}
                className="underline hover:no-underline font-medium"
              >
                Core Web Vitals
              </button>{' '}
              pour une vue d&eacute;taill&eacute;e des m&eacute;triques.
            </p>
          </div>
          <button
            onClick={() => setCompletedNotif(false)}
            className="p-1 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            <X className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* ── Progress (pending / running) ── */}
      {(activeAudit?.status === 'running' || activeAudit?.status === 'pending') && (
        <div className="max-w-xl bg-white border border-[#E2E4ED] rounded-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#6366F1] animate-pulse" strokeWidth={1.25} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1B2559]">
                {activeAudit.status === 'pending' ? 'Audit en attente...' : 'Audit en cours...'}
              </h2>
              {activeAudit.status === 'running' && progress && (
                <p className="text-sm text-[#8B8FA7]">
                  {progress.step === 'discovery'
                    ? 'Découverte des pages'
                    : `Audit PageSpeed (${progress.current}/${progress.total})`}
                </p>
              )}
            </div>
          </div>

          {activeAudit.status === 'running' && progress && (
            <>
              {/* Progress bar */}
              <div className="mb-4">
                {(() => {
                  const pct = progress.total > 0
                    ? Math.round((progress.current / progress.total) * 100)
                    : 0
                  return (
                    <>
                      <div className="flex justify-between text-xs text-[#8B8FA7] mb-1.5">
                        <span>
                          {progress.step === 'discovery'
                            ? 'Découverte...'
                            : `Page ${progress.current} sur ${progress.total}`}
                        </span>
                        <span>{pct}%</span>
                      </div>
                      <div className="w-full h-2 bg-[#F4F5FA] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#6366F1] to-[#7C3AED] rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </>
                  )
                })()}
              </div>

              {/* Current URL */}
              {progress.currentUrl && (
                <div className="flex items-center gap-2 text-xs text-[#555A73] bg-[#F4F5FA] px-3 py-2 rounded-lg">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" strokeWidth={1.25} />
                  <span className="truncate">{progress.currentUrl}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Completed results ── */}
      {isCompleted && (
        <>
          {/* Wait for detail to load */}
          {!auditDetail ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex items-center gap-3 text-[#8B8FA7]">
                <div className="w-5 h-5 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Chargement des résultats...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Opportunities */}
              {opportunities.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-[#1B2559] mb-3">
                    Opportunités d'amélioration
                  </h2>
                  <OpportunityAccordion opportunities={opportunities} />
                </div>
              )}

              {/* Pages table */}
              <div>
                <h2 className="text-lg font-semibold text-[#1B2559] mb-3 mt-6">
                  Pages auditées ({pages.length})
                </h2>
                <PagesTable
                  pages={pages}
                  projectId={projectId ?? ''}
                  auditId={selectedAuditId!}
                  previousPages={previousPages}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* ── Failed state ── */}
      {activeAudit?.status === 'failed' && (
        <div className="max-w-xl bg-white border border-red-200 rounded-xl p-8 text-center">
          <p className="text-[#1B2559] font-semibold mb-2">Audit échoué</p>
          <p className="text-sm text-[#8B8FA7] mb-4">{activeAudit.errorMsg ?? 'Erreur inconnue'}</p>
        </div>
      )}
    </div>
  )
}
